/**
 * Optional soft sound cues, generated with WebAudio (no audio assets).
 *
 * Rules honoured here:
 * - capability-checked: no AudioContext, no cues
 * - setting-gated: disabled instances are complete no-ops
 * - NO autoplay: the AudioContext is only created/resumed inside unlock(),
 *   which must be called synchronously from the user's Start tap.
 */

export type SoundCues = {
  /** Create/resume the AudioContext. Call from a user gesture only. */
  unlock: () => void
  cueIn: () => void
  cueHold: () => void
  cueOut: () => void
  cueDone: () => void
  dispose: () => void
}

type AudioContextCtor = new () => AudioContext

function resolveAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as Window & { webkitAudioContext?: AudioContextCtor }
  return window.AudioContext ?? w.webkitAudioContext ?? null
}

export function isAudioCueSupported(): boolean {
  return resolveAudioContextCtor() !== null
}

const PEAK_GAIN = 0.045

/** Gentle sine glide with a soft attack/release envelope. */
function playGlide(
  context: AudioContext,
  fromHz: number,
  toHz: number,
  seconds: number,
): void {
  const now = context.currentTime
  const oscillator = context.createOscillator()
  const gain = context.createGain()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(fromHz, now)
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, toHz), now + seconds)
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, now + Math.min(0.25, seconds / 3))
  gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds)
  oscillator.connect(gain)
  gain.connect(context.destination)
  oscillator.start(now)
  oscillator.stop(now + seconds + 0.05)
}

const NOOP_CUES: SoundCues = {
  unlock: () => undefined,
  cueIn: () => undefined,
  cueHold: () => undefined,
  cueOut: () => undefined,
  cueDone: () => undefined,
  dispose: () => undefined,
}

export function createSoundCues(enabled: boolean): SoundCues {
  const Ctor = enabled ? resolveAudioContextCtor() : null
  if (!Ctor) return NOOP_CUES

  let context: AudioContext | null = null

  const ensureContext = (): AudioContext | null => {
    if (!context) {
      try {
        context = new Ctor()
      } catch {
        return null
      }
    }
    if (context.state === 'suspended') {
      // Fire-and-forget; the next cue simply plays once resumed.
      void context.resume().catch(() => undefined)
    }
    return context
  }

  const play = (fromHz: number, toHz: number, seconds: number): void => {
    const ctx = ensureContext()
    if (!ctx || ctx.state !== 'running') return
    try {
      playGlide(ctx, fromHz, toHz, seconds)
    } catch {
      // Sound is decorative; never break the session over it.
    }
  }

  return {
    unlock: () => {
      ensureContext()
    },
    cueIn: () => play(330, 440, 1.1),
    cueHold: () => play(392, 392, 0.5),
    cueOut: () => play(440, 300, 1.3),
    cueDone: () => play(392, 523, 0.8),
    dispose: () => {
      if (context && context.state !== 'closed') {
        void context.close().catch(() => undefined)
      }
      context = null
    },
  }
}
